import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { Anchor, ShieldAlert, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function CrewRemoveApprove() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [state, setState] = useState("loading"); // loading | review | confirming | done | error
  const [info, setInfo] = useState(null);
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState(null);
  const [step, setStep] = useState("review"); // review | areYouSure

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) {
        setState("error");
        setErr("Missing token. Open the link from the email exactly as sent.");
        return;
      }
      try {
        const { data } = await api.get(`/yacht-members/remove-approve/info`, { params: { token } });
        if (!alive) return;
        setInfo(data);
        setState("review");
      } catch (e) {
        if (!alive) return;
        setErr(fmtErr(e));
        setState("error");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const proceedToConfirm = () => {
    if (info?.requires_otp && otp.trim().length < 6) {
      setErr("Enter the 6-digit verification code from the email.");
      return;
    }
    setErr(null);
    setStep("areYouSure");
  };

  const confirmRemoval = async () => {
    setState("confirming");
    setErr(null);
    try {
      const body = { token };
      if (info?.requires_otp) body.otp = otp.trim();
      const { data } = await api.post(`/yacht-members/remove-approve/confirm`, body);
      setInfo((p) => ({ ...p, yacht_name: data.yacht_name, removee_name: data.removee_name }));
      setState("done");
    } catch (e) {
      setErr(fmtErr(e));
      setState("review");
      setStep("review");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-xl mx-auto px-6 pt-12 pb-16">
        <Link to="/" className="inline-flex items-center gap-2 mb-8 text-ink">
          <div className="w-7 h-7 rounded-md bg-ink text-white grid place-items-center">
            <Anchor className="h-4 w-4" />
          </div>
          <span className="font-display text-[17px] font-medium">Seavanto</span>
        </Link>

        <div className="rounded-lg border border-zinc-200 bg-white p-8 shadow-sm" data-testid="crew-remove-card">
          {state === "loading" && (
            <div className="flex items-center gap-3 text-zinc-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Loading request…</span>
            </div>
          )}

          {state === "error" && (
            <div data-testid="crew-remove-error">
              <div className="flex items-center gap-2 text-red-700 mb-2">
                <AlertTriangle className="h-5 w-5" />
                <h1 className="font-display text-xl font-medium">Request unavailable</h1>
              </div>
              <p className="text-sm text-zinc-700">{err}</p>
              <Link to="/" className="text-sm text-ink underline mt-4 inline-block">Back to Seavanto</Link>
            </div>
          )}

          {state !== "loading" && state !== "error" && state !== "done" && info && step === "review" && (
            <div>
              <div className="flex items-center gap-2 text-zinc-800 mb-2">
                <ShieldAlert className="h-5 w-5 text-amber-600" />
                <h1 className="font-display text-xl font-medium">
                  {info.is_self_removal ? "Crew departure approval" : "Confirm your removal"}
                </h1>
              </div>
              <p className="text-sm text-zinc-600">
                {info.is_self_removal ? (
                  <>
                    Hi <strong>{info.approver_name}</strong>, <strong className="text-ink">{info.requester_name}</strong>{" "}
                    ({info.requester_role}) has requested to leave the crew of{" "}
                    <strong>{info.yacht_name}</strong>. As the senior on this yacht, your approval
                    is required to let them go.
                  </>
                ) : (
                  <>
                    Hi <strong>{info.removee_name}</strong>, <strong className="text-ink">{info.requester_name}</strong>{" "}
                    ({info.requester_role}) has requested to remove you from the crew of{" "}
                    <strong>{info.yacht_name}</strong>. You can approve this from here, or simply
                    ignore this email if you'd rather stay on the crew.
                  </>
                )}
              </p>

              <div className="mt-6 rounded-md border border-zinc-200 bg-zinc-50 p-4 space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-zinc-500">Yacht</span>
                  <span className="font-medium text-ink truncate">{info.yacht_name}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-zinc-500">
                    {info.is_self_removal ? "Crew member leaving" : "Requested by"}
                  </span>
                  <span className="font-medium text-ink truncate">
                    {info.is_self_removal ? info.removee_name : info.requester_name}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-zinc-500">
                    {info.is_self_removal ? "Their role" : "Your role"}
                  </span>
                  <span className="font-medium text-ink uppercase tracking-wider text-xs">
                    {info.removee_role}
                  </span>
                </div>
              </div>

              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                {info.is_self_removal ? (
                  <>
                    Approving will remove <strong>{info.removee_name}</strong> from{" "}
                    <strong>{info.yacht_name}</strong>'s crew. Their Seavanto account stays active,
                    they'll remain in your network, and any other yachts they're on are unaffected.
                  </>
                ) : (
                  <>
                    Approving will remove you from <strong>{info.yacht_name}</strong>'s crew. Your
                    Seavanto account stays active and any other yachts you're on are unaffected. You'll
                    still appear in {info.requester_name}'s network and can be re-invited later.
                  </>
                )}
              </div>

              {info.requires_otp && (
                <div className="mt-5">
                  <Label htmlFor="otp">Verification code</Label>
                  <p className="text-xs text-zinc-500 mb-2">
                    Enter the 6-digit code from the approval email.
                  </p>
                  <Input
                    id="otp"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    className="tracking-[0.4em] text-center text-lg font-mono"
                    data-testid="crew-remove-otp"
                  />
                </div>
              )}

              {err && <p className="mt-3 text-sm text-red-600" data-testid="crew-remove-err">{err}</p>}

              <div className="mt-6 flex items-center justify-end gap-2">
                <Link to="/" className="text-sm text-zinc-600 underline">
                  Decline (close window)
                </Link>
                <Button
                  onClick={proceedToConfirm}
                  disabled={info.requires_otp && otp.length < 6}
                  className="bg-ink hover:bg-black"
                  data-testid="crew-remove-continue"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {state !== "done" && info && step === "areYouSure" && (
            <div>
              <div className="flex items-center gap-2 text-amber-700 mb-2">
                <AlertTriangle className="h-5 w-5" />
                <h1 className="font-display text-xl font-medium">Are you sure?</h1>
              </div>
              <p className="text-sm text-zinc-700">
                {info.is_self_removal ? (
                  <>
                    Confirming will remove <strong>{info.removee_name}</strong> from the crew of{" "}
                    <strong>{info.yacht_name}</strong>. Their account stays and they can be re-invited any time.
                  </>
                ) : (
                  <>
                    Confirming will remove you from the crew of <strong>{info.yacht_name}</strong>. Your
                    account stays and you can be re-invited any time.
                  </>
                )}
              </p>

              {err && <p className="mt-3 text-sm text-red-600">{err}</p>}

              <div className="mt-6 flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setStep("review")} data-testid="crew-remove-back">
                  Go back
                </Button>
                <Button
                  onClick={confirmRemoval}
                  disabled={state === "confirming"}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="crew-remove-final"
                >
                  {state === "confirming"
                    ? "Approving…"
                    : info.is_self_removal
                      ? "Yes, approve their departure"
                      : "Yes, remove me"}
                </Button>
              </div>
            </div>
          )}

          {state === "done" && (
            <div data-testid="crew-remove-done">
              <div className="flex items-center gap-2 text-emerald-700 mb-2">
                <CheckCircle2 className="h-5 w-5" />
                <h1 className="font-display text-xl font-medium">Removal approved</h1>
              </div>
              <p className="text-sm text-zinc-700">
                You've been removed from <strong>{info?.yacht_name}</strong>. Your Seavanto account remains
                active.
              </p>
              <Link to="/" className="text-sm text-ink underline mt-4 inline-block">Back to Seavanto</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
